/**
 * About Section Initialization Script
 * Handles odometer counter and video popup initialization
 */

declare global {
    interface Window {
        Odometer?: any;
        showVideoModal?: (url: string) => void;
    }
    const $: any;
}

/**
 * Initialize odometer counter for experience years
 * Uses IntersectionObserver to trigger animation when element comes into view
 */
export function initializeOdometer(containerElement: HTMLElement | null): void {
    if (!containerElement) {
        return;
    }


    let retryCount = 0;
    const maxRetries = 50; // Try for 5 seconds (50 * 100ms)

    const initOdometer = (): void => {
        // Check if Odometer is available
        const OdometerClass = (window as any).Odometer;
        
        if (typeof OdometerClass === 'undefined' && retryCount < maxRetries) {
            retryCount++;
            if (retryCount === 1 || retryCount % 10 === 0) {
            }
            setTimeout(initOdometer, 100);
            return;
        }

        if (typeof OdometerClass === 'undefined') {
            return;
        }


        setTimeout(() => {
            try {
                const odometerElement = containerElement.querySelector('.odometer') as HTMLElement;
                
                if (!odometerElement) {
                    return;
                }


                const countNumber = odometerElement.getAttribute('data-count');
                if (!countNumber) {
                    return;
                }


                // Check if already initialized
                if ((odometerElement as any).odometer) {
                    (odometerElement as any).odometer.update(parseInt(countNumber));
                    return;
                }

                // Function to actually initialize and animate the odometer
                const startOdometer = () => {
                    try {
                        // Use the Odometer class we found
                        const OdometerConstructor = (window as any).Odometer;
                        
                        if (!OdometerConstructor) {
                            return;
                        }

                        // Set initial value in the element
                        odometerElement.innerHTML = '00';

                        // Initialize odometer
                        const odometerInstance = new OdometerConstructor({
                            el: odometerElement,
                            value: 0,
                            format: '(,ddd)',
                            duration: 2000,
                            theme: 'default'
                        });

                        // Store reference
                        (odometerElement as any).odometer = odometerInstance;

                        // Small delay to ensure initialization is complete
                        setTimeout(() => {
                            // Update to target value (this triggers the animation)
                            const targetValue = parseInt(countNumber);
                            odometerInstance.update(targetValue);
                        }, 100);
                    } catch (error) {
                    }
                };

                // Check if element is already visible in viewport
                const checkVisibility = () => {
                    const rect = odometerElement.getBoundingClientRect();
                    const isVisible = rect.top < window.innerHeight * 0.9 && rect.bottom > 0;
                    return isVisible;
                };

                // If already visible, start immediately
                if (checkVisibility()) {
                    setTimeout(startOdometer, 300);
                } else {
                    // Use IntersectionObserver to trigger when element comes into view
                    if ('IntersectionObserver' in window) {
                        const observer = new IntersectionObserver(
                            (entries) => {
                                entries.forEach((entry) => {
                                    if (entry.isIntersecting && !(odometerElement as any).odometer) {
                                        startOdometer();
                                        observer.disconnect(); // Only trigger once
                                    }
                                });
                            },
                            {
                                threshold: 0.1, // Trigger when 10% of element is visible
                                rootMargin: '50px' // Start a bit earlier
                            }
                        );
                        observer.observe(odometerElement);
                    } else {
                        // Fallback: use scroll event listener for older browsers
                        if (typeof window !== 'undefined' && typeof (window as any).addEventListener === 'function') {
                            let hasAnimated = false;
                            const scrollHandler = () => {
                                if (!hasAnimated && checkVisibility()) {
                                    hasAnimated = true;
                                    startOdometer();
                                    (window as Window).removeEventListener('scroll', scrollHandler);
                                }
                            };
                            (window as Window).addEventListener('scroll', scrollHandler);
                            // Also check immediately in case already in view
                            if (checkVisibility()) {
                                scrollHandler();
                            }
                        }
                    }
                }
            } catch (error) {
            }
        }, 500);
    };

    initOdometer();
}

/**
 * Initialize video popup for about section
 */
export function initializeVideoPopup(containerElement: HTMLElement | null): void {
    if (!containerElement) {
        return;
    }

    setTimeout(() => {
        try {
            const videoLink = containerElement.querySelector('.about-one__video-link .video-popup') as HTMLAnchorElement;
            
            if (!videoLink) {
                return;
            }

            const videoUrl = videoLink.getAttribute('data-video-url') || videoLink.href;

            // Check if it's a YouTube URL
            const isYouTubeUrl = videoUrl && (
                videoUrl.includes('youtube.com') || 
                videoUrl.includes('youtu.be')
            );

            // Always use Magnific Popup for all videos (YouTube modal)
                const jQuery = (window as any).jQuery;
                if (typeof jQuery !== 'undefined' && jQuery.fn && jQuery.fn.magnificPopup) {
                // Convert to YouTube URL if not already YouTube
                let youtubeUrl = videoUrl;
                
                if (!isYouTubeUrl) {
                    // Use default YouTube video for non-YouTube URLs
                    youtubeUrl = 'https://www.youtube.com/watch?v=CR9Y3Do-aX8';
                }
                
                // Extract video ID from YouTube URL
                let videoId = '';
                const patterns = [
                    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/,
                    /[?&]v=([a-zA-Z0-9_-]+)/
                ];
                
                for (const pattern of patterns) {
                    const match = youtubeUrl.match(pattern);
                    if (match && match[1]) {
                        videoId = match[1].split('&')[0].split('?')[0].split('#')[0];
                        break;
                    }
                }
                
                // Store video ID in data attribute and use javascript:void(0) to prevent navigation
                const finalVideoId = videoId || 'CR9Y3Do-aX8';
                videoLink.setAttribute('data-youtube-id', finalVideoId);
                videoLink.href = 'javascript:void(0)'; // Use javascript:void(0) to prevent any navigation or scroll
                
                
                // Store scroll position
                let scrollPosition = 0;
                
                // Get the embed URL
                const embedUrl = `https://www.youtube.com/embed/${finalVideoId}?autoplay=1&mute=1&rel=0&enablejsapi=1&origin=${window.location.origin}`;
                
                // Prevent default link behavior and lock scroll position IMMEDIATELY
                videoLink.addEventListener('click', function(e) {
                    
                    // CRITICAL: Lock scroll FIRST, before preventDefault
                    const initialScroll = window.pageYOffset || document.documentElement.scrollTop;
                    scrollPosition = initialScroll;
                    
                    // Lock scroll IMMEDIATELY and synchronously
                    document.body.style.position = 'fixed';
                    document.body.style.top = `-${scrollPosition}px`;
                    document.body.style.width = '100%';
                    document.body.style.overflow = 'hidden';
                    document.documentElement.style.overflow = 'hidden';
                    document.documentElement.style.position = 'fixed';
                    document.documentElement.style.top = `-${scrollPosition}px`;
                    
                    // Check scroll position after lock
                    setTimeout(() => {
                        const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
                        if (currentScroll !== 0 && currentScroll !== scrollPosition) {
                        }
                    }, 10);
                    
                    // Prevent ALL default behaviors
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    
                    // Prevent any scroll events
                    let scrollEventCount = 0;
                    const preventScroll = (e: Event) => {
                        scrollEventCount++;
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                    };
                    window.addEventListener('scroll', preventScroll, { passive: false, capture: true });
                    window.addEventListener('wheel', preventScroll, { passive: false, capture: true });
                    window.addEventListener('touchmove', preventScroll, { passive: false, capture: true });
                    
                    // Monitor scroll position continuously
                    const scrollMonitor = setInterval(() => {
                        const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
                        if (currentScroll !== 0 && currentScroll !== scrollPosition) {
                        }
                    }, 50);
                    
                    // Store the prevent functions to remove later
                    const removeScrollPrevention = () => {
                        clearInterval(scrollMonitor);
                        window.removeEventListener('scroll', preventScroll, { capture: true });
                        window.removeEventListener('wheel', preventScroll, { capture: true });
                        window.removeEventListener('touchmove', preventScroll, { capture: true });
                    };
                    
                    // Open popup with direct URL
                    jQuery.magnificPopup.open({
                        items: {
                            src: embedUrl,
                            type: 'iframe'
                        },
                        mainClass: 'mfp-fade',
                        removalDelay: 160,
                        preloader: false,
                        fixedContentPos: true,
                        iframe: {
                            patterns: {
                                youtube: {
                                    index: 'youtube.com/',
                                    id: finalVideoId,
                                    src: embedUrl
                                }
                            }
                        },
                        callbacks: {
                            beforeOpen: function() {
                                const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
                                
                                // Double-check scroll is locked (should already be from click)
                                if (scrollPosition === 0) {
                                    scrollPosition = currentScroll;
                                }
                                
                                // Force lock again
                                document.body.style.position = 'fixed';
                                document.body.style.top = `-${scrollPosition}px`;
                                document.body.style.width = '100%';
                                document.body.style.overflow = 'hidden';
                                document.documentElement.style.position = 'fixed';
                                document.documentElement.style.top = `-${scrollPosition}px`;
                                document.documentElement.style.overflow = 'hidden';
                                
                                // Check again after a moment
                                setTimeout(() => {
                                    const checkScroll = window.pageYOffset || document.documentElement.scrollTop;
                                    if (checkScroll !== 0 && checkScroll !== scrollPosition) {
                                    }
                                }, 100);
                            },
                            open: function() {
                                document.body.classList.add('mfp-body-lock');
                                document.documentElement.classList.add('mfp-body-lock');
                                
                                setTimeout(() => {
                                    const checkScroll = window.pageYOffset || document.documentElement.scrollTop;
                                    if (checkScroll !== 0 && checkScroll !== scrollPosition) {
                                    }
                                }, 100);
                            },
                            afterOpen: function() {
                                // Ensure video starts playing immediately and unmute
                                const iframe = jQuery('.mfp-iframe')[0] as HTMLIFrameElement;
                                if (iframe && iframe.contentWindow) {
                                    const playVideo = () => {
                                        try {
                                            iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
                                            setTimeout(() => {
                                                iframe.contentWindow?.postMessage('{"event":"command","func":"unMute","args":""}', '*');
                                            }, 500);
                                        } catch (e) {
                                        }
                                    };
                                    playVideo();
                                    setTimeout(playVideo, 100);
                                    setTimeout(playVideo, 500);
                                }
                            },
                            close: function() {
                                // Remove scroll prevention
                                removeScrollPrevention();
                                
                                document.body.classList.remove('mfp-body-lock');
                                document.documentElement.classList.remove('mfp-body-lock');
                                document.body.style.position = '';
                                document.body.style.top = '';
                                document.body.style.width = '';
                                document.body.style.overflow = '';
                                document.documentElement.style.position = '';
                                document.documentElement.style.top = '';
                                document.documentElement.style.overflow = '';
                                
                                requestAnimationFrame(() => {
                                    window.scrollTo(0, scrollPosition);
                                    scrollPosition = 0;
                                });
                            }
                        }
                    });
                    
                    return false;
                }, true);
                

                    // Add touch event support for mobile
                    videoLink.addEventListener('touchstart', function(e) {
                        e.stopPropagation();
                        // Lock scroll immediately on touch
                        scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
                        document.body.style.position = 'fixed';
                        document.body.style.top = `-${scrollPosition}px`;
                        document.body.style.width = '100%';
                        document.body.style.overflow = 'hidden';
                        document.documentElement.style.overflow = 'hidden';
                    }, { passive: false });
                    videoLink.addEventListener('touchend', function(e) {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation();
                        // Ensure scroll is locked
                        scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
                        document.body.style.position = 'fixed';
                        document.body.style.top = `-${scrollPosition}px`;
                        document.body.style.width = '100%';
                        document.body.style.overflow = 'hidden';
                        document.documentElement.style.overflow = 'hidden';
                        // Open popup with direct URL
                        setTimeout(() => {
                            jQuery.magnificPopup.open({
                                items: {
                                    src: embedUrl,
                                    type: 'iframe'
                                },
                                mainClass: 'mfp-fade',
                                removalDelay: 160,
                                preloader: false,
                                fixedContentPos: true,
                                iframe: {
                                    patterns: {
                                        youtube: {
                                            index: 'youtube.com/',
                                            id: finalVideoId,
                                            src: embedUrl
                                        }
                                    }
                                },
                                callbacks: {
                                    beforeOpen: function() {
                                        if (scrollPosition === 0) {
                                            scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
                                        }
                                        document.body.style.position = 'fixed';
                                        document.body.style.top = `-${scrollPosition}px`;
                                        document.body.style.width = '100%';
                                        document.body.style.overflow = 'hidden';
                                        document.documentElement.style.overflow = 'hidden';
                                    },
                                    open: function() {
                                        document.body.classList.add('mfp-body-lock');
                                        document.documentElement.classList.add('mfp-body-lock');
                                    },
                                    afterOpen: function() {
                                        const iframe = jQuery('.mfp-iframe')[0] as HTMLIFrameElement;
                                        if (iframe && iframe.contentWindow) {
                                            const playVideo = () => {
                                                try {
                                                    iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
                                                    setTimeout(() => {
                                                        iframe.contentWindow?.postMessage('{"event":"command","func":"unMute","args":""}', '*');
                                                    }, 500);
                                                } catch (e) {
                                                }
                                            };
                                            playVideo();
                                            setTimeout(playVideo, 100);
                                            setTimeout(playVideo, 500);
                                        }
                                    },
                                    close: function() {
                                        document.body.classList.remove('mfp-body-lock');
                                        document.documentElement.classList.remove('mfp-body-lock');
                                        document.body.style.position = '';
                                        document.body.style.top = '';
                                        document.body.style.width = '';
                                        document.body.style.overflow = '';
                                        document.documentElement.style.overflow = '';
                                        requestAnimationFrame(() => {
                                            window.scrollTo(0, scrollPosition);
                                            scrollPosition = 0;
                                        });
                                    }
                                }
                            });
                        }, 10);
                    }, { passive: false });

                } else {
            }
        } catch (error) {
        }
    }, 500);
}


/**
 * Initialize all about section features
 */
export function initializeAboutSection(containerElement: HTMLElement | null): void {
    initializeOdometer(containerElement);
    initializeVideoPopup(containerElement);
}

